<template>
  <nav
    v-if="labelOnly"
    class="c-pagination c-pagination--label"
    aria-label="Pagination"
  >
    <button
      type="button"
      class="c-pagination__step"
      title="First page"
      :disabled="disabled || value <= 1"
      @click="go(1)"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
        aria-hidden="true"
      >
        <path d="m11 17-5-5 5-5" /><path d="m18 17-5-5 5-5" />
      </svg>
    </button>
    <button
      type="button"
      class="c-pagination__step"
      title="Previous page"
      :disabled="disabled || value <= 1"
      @click="go(value - 1)"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
        aria-hidden="true"
      >
        <path d="m15 18-6-6 6-6" />
      </svg>
    </button>
    <span class="c-pagination__label">Page {{ value }} of {{ pageCount }}</span>
    <button
      type="button"
      class="c-pagination__step"
      title="Next page"
      :disabled="disabled || value >= pageCount"
      @click="go(value + 1)"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
        aria-hidden="true"
      >
        <path d="m9 18 6-6-6-6" />
      </svg>
    </button>
    <button
      type="button"
      class="c-pagination__step"
      title="Last page"
      :disabled="disabled || value >= pageCount"
      @click="go(pageCount)"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
        aria-hidden="true"
      >
        <path d="m6 17 5-5-5-5" /><path d="m13 17 5-5-5-5" />
      </svg>
    </button>
  </nav>
  <b-pagination
    v-else
    :key="pagerKey"
    class="c-pagination"
    pills
    align="center"
    :value="value"
    :total-rows="totalRows"
    :per-page="perPage"
    :disabled="disabled"
    @input="$emit('input', $event)"
    @change="$emit('change', $event)"
  >
    <template #first-text>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
        aria-hidden="true"
      >
        <path d="m17 18-6-6 6-6" /><path d="m11 18-6-6 6-6" />
      </svg>
    </template>
    <template #prev-text>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
        aria-hidden="true"
      >
        <path d="m15 18-6-6 6-6" />
      </svg>
    </template>
    <template #next-text>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
        aria-hidden="true"
      >
        <path d="m9 18 6-6-6-6" />
      </svg>
    </template>
    <template #page="{ content }">
      <span class="c-pagination__digit">{{ content }}</span>
    </template>
    <template #last-text>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
        aria-hidden="true"
      >
        <path d="m7 18 6-6-6-6" /><path d="m13 18 6-6-6-6" />
      </svg>
    </template>
    <slot />
  </b-pagination>
</template>

<script>
export default {
  name: 'ItemPagination',
  props: {
    value: {
      type: Number,
      default: 1,
    },
    totalRows: {
      type: Number,
      default: 0,
    },
    perPage: {
      type: Number,
      default: 10,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    labelOnly: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    // b-pagination clamps `value` against its previous page count; a remount reads both together.
    pagerKey() {
      return `${this.perPage}-${this.totalRows}`;
    },
    pageCount() {
      if (!this.perPage || !this.totalRows) {
        return 1;
      }

      return Math.max(1, Math.ceil(this.totalRows / this.perPage));
    },
  },
  methods: {
    go(page) {
      const next = Math.min(this.pageCount, Math.max(1, page));

      if (next === this.value) {
        return;
      }

      this.$emit('input', next);
      this.$emit('change', next);
    },
  },
};
</script>

<style scoped lang="scss">
.c-pagination {
  width: fit-content;
  // No bottom margin: each host owns the space under the pager (card padding, modal footer gap).
  margin: 16px auto 0;

  ::v-deep .page-item {
    margin: 0 2px;

    .page-link {
      display: flex;
      align-items: center;
      justify-content: center;
      min-width: 28px;
      height: 28px;
      padding: 0 6px;
      border: 0;
      border-radius: 14px !important;
      background-color: transparent;
      color: #64748b;
      font-family: "Nunito", sans-serif;
      font-size: 14px;
      font-weight: 500;
      white-space: nowrap;
      transition: background-color 0.15s, color 0.15s;
    }

    // hsl(210 40% 96.1%) — the design's `--muted`.
    .page-link:hover {
      background-color: #f1f5f9;
      color: rgb(34, 38, 42);
    }

    .page-link:focus {
      box-shadow: 0 0 0 3px rgba(22, 163, 139, 0.15);
    }
  }

  // Nunito's ascent/descent split leaves a digit sitting a pixel above the pill's centre.
  ::v-deep .c-pagination__digit {
    position: relative;
    top: 1px;
  }

  ::v-deep .page-item.active .page-link {
    min-width: 28px;
    height: 28px;
    background-color: #16a38b;
    color: #ffffff;
    font-weight: 600;
  }

  ::v-deep .page-item.active .page-link:hover {
    background-color: #14937d;
  }

  ::v-deep .page-item.disabled .page-link {
    opacity: 0.35;
    background-color: transparent;
    cursor: not-allowed;
  }
}

.c-pagination--label {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  margin: 4px auto 0;
}

.c-pagination__step {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: #64748b;

  &:hover:not(:disabled) {
    background: #f1f5f9;
    color: #0f172a;
  }

  &:disabled {
    opacity: 0.5;
    pointer-events: none;
  }
}

.c-pagination__label {
  padding: 0 8px;
  color: #64748b;
  font-family: "Nunito", sans-serif;
  font-size: 12px;
  font-variant-numeric: tabular-nums;
  line-height: 28px;
}
</style>
